import { create } from 'zustand'
import { wishlistService } from '../services/api'

/**
 * Server-driven wishlist store.
 * - items: array of enriched wishlist items from GET /wishlist
 *   Each item has: { id (wishlist_item_id), product_id, description, brand,
 *                    primary_image, inclprice2, stock_code, active, ... }
 * - productIds: derived Set of product_id values for quick isInWishlist checks
 * - Call syncFromServer() on login / page load to hydrate
 */
export const useWishlistStore = create((set, get) => ({
  items: [],          // enriched server items
  loading: false,

  // Sync from server — call after login
  syncFromServer: async () => {
    set({ loading: true })
    try {
      const data = await wishlistService.get()
      const items = data?.items ?? []
      set({ items, loading: false })
    } catch {
      set({ loading: false })
    }
  },

  // Toggle a product in/out of wishlist
  toggle: async (product) => {
    const items = get().items
    const existing = items.find(i => i.product_id === product.id)

    if (existing) {
      // Optimistically remove
      set({ items: items.filter(i => i.product_id !== product.id) })
      try {
        await wishlistService.removeByProduct(product.id)
      } catch {
        // Rollback on failure
        set({ items: get().items.concat(existing) })
      }
    } else {
      // Optimistically add a placeholder
      const placeholder = {
        id: null,
        product_id: product.id,
        description: product.description || product.product_name,
        brand: product.brand,
        primary_image: product.primary_image,
        inclprice2: parseFloat(product.inclprice2 || product.price || 0),
        stock_code: product.stock_code,
        active: true,
        _pending: true,
      }
      set({ items: [...items, placeholder] })
      try {
        await wishlistService.add(product.id)
        // Refresh to get the real wishlist_item_id from server
        const data = await wishlistService.get()
        set({ items: data?.items ?? [] })
      } catch {
        // Rollback
        set({ items: get().items.filter(i => i.product_id !== product.id) })
      }
    }
  },

  isInWishlist: (productId) => get().items.some(i => i.product_id === productId),

  // Direct remove by wishlist_item_id (from wishlist page)
  removeItem: async (wishlistItemId, productId) => {
    const items = get().items
    set({ items: items.filter(i => i.id !== wishlistItemId) })
    try {
      await wishlistService.removeByProduct(productId)
    } catch {
      set({ items })
    }
  },

  clear: () => set({ items: [] }),
}))
